//@flow
import React, {useEffect, useState} from 'react';

import {
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import CustomSelect from '../../../../../../elements/CustomSelect/CustomSelect';
import {PRODUCT_FEATURES} from '../../../../../products/utils';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import {DeleteOutline} from '@material-ui/icons';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {useEditProduct} from '../../../../utils/editProduct';
import {useProductContext} from '../../../../context';
import {useProductSpecifications} from '../../../../../products/utils/hooks';
import addProductFeatureIcon from '../../../../../../assets/img/add-product-feature.svg';
import {colors} from '../../../../../../assets/styles/colors';

const useStyles = makeStyles(theme => ({
  modalRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 600,
    width: '90vw',
    gap: '1.5rem',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
    color: colors.grey800,
  },
  submitButton: {
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },

  content: {
    width: '100%',
    height: '100%',
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'hidden auto',
  },

  sectionSubtitle: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
    '& span': {
      color: colors.grey900,
      fontWeight: 500,
    },
  },

  featuresHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  wrapSelect: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  select: {
    height: 28,
  },
  selectItem: {
    boxSizing: 'border-box',
    margin: theme.spacing(0, 1),
    borderRadius: '6px',
    maxWidth: '100%',
  },
  wrapIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  listProductFeatures: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  wrapProductFeature: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    background: colors.grey100,
    boxShadow:
      'inset 0px -1px 4px rgba(0, 0, 0, 0.04), inset 0px 1px 4px rgba(0, 0, 0, 0.08)',
  },
  addFeatureButton: {
    borderRadius: 8,
    width: '100%',
    border: '1px dashed #B8B8C7',
    paddingTop: theme.spacing(2.25),
    paddingBottom: theme.spacing(2.25),
    '& img': {
      marginRight: theme.spacing(1),
    },
  },
  wrapSubtitleSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {},
  },
  icon: {
    marginLeft: '1rem',
  },

  deleteFeatureBtn: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
}));

type ProductFeature = {
  name: string,
  description: string,
};

const defaultField: ProductFeature = {
  description: '',
  name: '',
};

type Props = {
  onSubmit: (featuresResult: string) => void,
};

export default function ProductFeatureModal(props: Props) {
  const classes = useStyles();
  const {
    fetchSpecifications,
    specificationSetting,
  } = useProductSpecifications();

  const [productId] = useProductContext(product => product.id);
  const {editProduct} = useEditProduct(productId);

  const {control, register, errors, handleSubmit} = useForm();
  const {fields, append, remove, prepend} = useFieldArray<ProductFeature>({
    control,
    name: 'productFeatures',
  });

  const [showIcons] = useState(true);

  useEffect(() => {
    if (!productId) return;

    fetchSpecifications(productId);
  }, [fetchSpecifications, productId]);

  useEffect(() => {
    if (!specificationSetting) return;

    const cleanProductFeatures = specificationSetting.specifications.map(
      ({name, description}) => ({
        name,
        description,
      })
    );
    prepend(cleanProductFeatures);
  }, [specificationSetting, prepend]);

  const onSubmit = data => {
    const productFeatures =
      data.productFeatures &&
      data.productFeatures.filter(item => !!item.name && !!item.description);

    editProduct({
      specificationSetting: {
        enabled: true,
        specifications: productFeatures || [],
      },
    });

    const featuresResult = productFeatures
      ?.map(feature => `${feature.name}: ${feature.description}`)
      .join('; ');

    props.onSubmit(featuresResult || '');
  };

  return (
    <aside className={classes.modalRoot}>
      <Typography className={classes.title} align="center">
        Características de producto
      </Typography>
      <div className={classes.content}>
        <header className={classes.featuresHeader}>
          <Typography className={classes.sectionSubtitle}>
            Máximo 28 caracteres - incluyendo espacios
          </Typography>
          {/*       <FormControlLabel
            control={
              <Checkbox
                checked={showIcons}
                onChange={toggleShowIcons}
                name="checkedI"
                color="primary"
              />
            }
            className={classes.sectionCheckbox}
            label="Mostrar iconos"
          /> */}
        </header>

        <Grid container spacing={2}>
          {fields
            .filter(productFeature => productFeature.id)
            .map((productFeature, idx) => (
              <Grid item xs={12} key={productFeature.id}>
                <div className={classes.wrapProductFeature}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        control={control}
                        name={`productFeatures.${idx}.name`}
                        defaultValue={productFeature.name || ''}
                        rules={{required: true}}
                        as={
                          <CustomSelect
                            classes={{root: classes.select}}
                            fullWidth
                          >
                            <option hidden />
                            {PRODUCT_FEATURES.map(({name, icon}, index) => (
                              <MenuItem
                                name={name}
                                className={classes.selectItem}
                                key={name}
                                value={name}
                              >
                                <div className={classes.wrapIcon}>
                                  {name}
                                  {showIcons && icon ? (
                                    <img
                                      src={icon}
                                      className={classes.icon}
                                      alt={'icon ' + name}
                                    />
                                  ) : null}
                                </div>
                              </MenuItem>
                            ))}
                          </CustomSelect>
                        }
                      ></Controller>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        label="Escribe aquí la descripción"
                        name={`productFeatures.${idx}.description`}
                        defaultValue={productFeature.description}
                        error={
                          !!errors.productFeatures &&
                          !!errors.productFeatures[idx]
                        }
                        helperText={
                          errors.productFeatures &&
                          errors.productFeatures[idx] &&
                          errors.productFeatures[idx].description
                            ? errors.productFeatures[idx].description.message
                            : ''
                        }
                        type="text"
                        inputRef={register({
                          required: 'Campo obligatorio',
                          maxLength: {
                            value: 28,
                            message: 'Máximo 28 caracteres',
                          },
                        })}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              edge="end"
                              onClick={() => {
                                remove(idx);
                              }}
                            >
                              <DeleteOutline color="error" />
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            ))}
          <Grid item xs={12}>
            <CustomButton
              className={classes.addFeatureButton}
              onClick={() => {
                append(defaultField);
              }}
              disabled={fields.length >= 6}
            >
              <img
                style={{opacity: fields.length >= 6 ? 0.2 : 1}}
                src={addProductFeatureIcon}
                alt="add product feature"
              />
              Agregar otra característica
            </CustomButton>
          </Grid>
        </Grid>
      </div>
      <CustomButton
        className={classes.submitButton}
        color="primary"
        variant="contained"
        fullWidth
        onClick={handleSubmit(onSubmit)}
      >
        Guardar
      </CustomButton>
    </aside>
  );
}
